/**
 * Every word anybody says at the table, in one file.
 *
 * ============================= WHAT THIS IS ====================================
 * Phase 6's taunts. One file so the whole of the table's voice can be read in a
 * sitting — the plan's own instruction — and so `tests/voice-copy.test.ts` can hold
 * the four rules that govern it:
 *
 *   1. **Nothing here is an advertisement.** §7 of the master plan names the four
 *      surfaces internal marketing is allowed on: the seat card, the Ledger, the
 *      Barkeep and the hand-end verdict. A taunt is not one of them. A taunt is a
 *      character talking, and a character has never heard of the trainer.
 *   2. **Nothing here makes a claim.** No line says a play was right, optimal, solved
 *      or a chart. These are people at a card table, not the grader.
 *   3. **Nothing here is cruel.** This is a saloon, not a comments section. Lines are
 *      about the speaker and their own cards. A line that tells the player they are
 *      bad at this does not go in, however funny it is the first time.
 *   4. **Everything here fits a bubble on a phone.** Thirty-four characters is about
 *      what a 367px felt gives a seat's bubble at two lines, and the test holds it.
 *
 * ## Three kinds of taunt, and where each comes from
 *
 *   - a STICKER — the twelve western reactions in `ART-PROMPTS-2026-09-05.md` §6.
 *     Tim's renders are not in yet, so `public/art/stickers/stickers.json` ships with
 *     an empty set and the tray shows the emoji row alone. Nothing here draws a
 *     placeholder sticker: a placeholder that looks finished is how a placeholder
 *     ships.
 *   - an EMOJI — one fixed row of eight, no picker, no search, no skin tones.
 *   - a LINE — the three seeded `taunt` cosmetics from `0007_cosmetics.sql`, plus a
 *     set of six per archetype below.
 *
 * ## Why the line sets are per ARCHETYPE, and what that costs
 *
 * The plan asks for them in character, and a character here is an archetype: the
 * Rock and the Maniac are different people. The cost is real and is raised as an open
 * question in the report — a player who learns these thirty lines can read an
 * archetype off a bubble, and the archetype is the thing the product otherwise hides
 * until the Ledger. It is a soft tell rather than a leak (the frequencies say more
 * than the words), and tells are the game; but it IS information, and it was not
 * information before this phase.
 * ===============================================================================
 */

/** The nine archetypes, by the id `engine/bot/profile.ts` gives them. */
export type ArchetypeId =
  | "whale" | "station" | "maniac" | "rec" | "nit" | "tag" | "lag" | "grinder" | "expert";

/**
 * What a line is FOR.
 *
 * `up` — a pot won, a steal, a double-up. `down` — busted. `nudge` — the player's
 * clock ran out and somebody says something about it. Every archetype has all three,
 * so no trigger can ever find itself with nothing to say.
 */
export type Mood = "up" | "down" | "nudge";

export interface Line { mood: Mood; text: string }

/** Six lines each: three up, two down, one nudge. */
export const ARCHETYPE_LINES: Readonly<Record<ArchetypeId, readonly Line[]>> = {
  whale: [
    { mood: "up", text: "Well now, look at that." },
    { mood: "up", text: "I had a feelin' about them." },
    { mood: "up", text: "Never met a hand I didn't like." },
    { mood: "down", text: "Fun while it lasted." },
    { mood: "down", text: "That's the game, I reckon." },
    { mood: "nudge", text: "You still with us, friend?" },
  ],
  station: [
    { mood: "up", text: "Told you I'd call." },
    { mood: "up", text: "Had to see it." },
    { mood: "up", text: "Curiosity paid, this once." },
    { mood: "down", text: "Paid to find out. Worth it." },
    { mood: "down", text: "Out of chips, not questions." },
    { mood: "nudge", text: "No rush. I'll wait." },
  ],
  maniac: [
    { mood: "up", text: "Told you I don't slow down." },
    { mood: "up", text: "Fire wins pots, partner." },
    { mood: "up", text: "Pressure. Every time." },
    { mood: "down", text: "Went out swinging." },
    { mood: "down", text: "Burned bright, didn't I." },
    { mood: "nudge", text: "Clock's tickin', partner." },
  ],
  rec: [
    { mood: "up", text: "Got there on the river!" },
    { mood: "up", text: "Knew that card was comin'." },
    { mood: "up", text: "Sometimes you just feel it." },
    { mood: "down", text: "Missed it by one card." },
    { mood: "down", text: "Draw didn't come. Happens." },
    { mood: "nudge", text: "Your go, friend." },
  ],
  nit: [
    { mood: "up", text: "I bet when I have it." },
    { mood: "up", text: "Waited all night for that." },
    { mood: "up", text: "Patience, is all." },
    { mood: "down", text: "Even a rock cracks." },
    { mood: "down", text: "Wrong night to have a hand." },
    { mood: "nudge", text: "Whenever you're ready." },
  ],
  tag: [
    { mood: "up", text: "Standard enough." },
    { mood: "up", text: "That's the line." },
    { mood: "up", text: "Nothing fancy about it." },
    { mood: "down", text: "Ran straight into it." },
    { mood: "down", text: "Good hand, wrong night." },
    { mood: "nudge", text: "Still your action." },
  ],
  lag: [
    { mood: "up", text: "You were never calling." },
    { mood: "up", text: "That's the spot, every time." },
    { mood: "up", text: "Pressure's the whole game." },
    { mood: "down", text: "Caught me. Nice hand." },
    { mood: "down", text: "Ran out of road." },
    { mood: "nudge", text: "Clock, partner." },
  ],
  grinder: [
    { mood: "up", text: "Small edges, all night." },
    { mood: "up", text: "That one was mine." },
    { mood: "up", text: "Adds up, doesn't it." },
    { mood: "down", text: "Variance. Back tomorrow." },
    { mood: "down", text: "Cost of doing business." },
    { mood: "nudge", text: "Your call to make." },
  ],
  expert: [
    { mood: "up", text: "That was the play." },
    { mood: "up", text: "Right price, right spot." },
    { mood: "up", text: "Reckon that's about right." },
    { mood: "down", text: "Well played. Truly." },
    { mood: "down", text: "You had it. Good." },
    { mood: "nudge", text: "Take your time." },
  ],
};

/* ------------------------------------------------------------ the catalogue --- */

export type TauntKind = "sticker" | "emoji" | "line";

export interface TauntItem {
  /** Stable id. Cosmetic taunts use the `cosmetics` table's own id. */
  id: string;
  kind: TauntKind;
  /** What the tray's button is labelled and what a screen reader says. */
  label: string;
  /** What the bubble draws: the emoji, or the line. A sticker draws its image. */
  text: string;
  /** Stickers: the manifest id, if the render has landed. */
  sticker?: string;
}

/**
 * The emoji row. One row of eight, in this order, and there is no picker.
 *
 * Eight is what fits a phone at a 44px target with the sheet's own padding, and a
 * fixed row is a decision rather than a feature: a full picker at a poker table is a
 * way to spend eight seconds of a ten-second clock choosing a face.
 */
export const EMOJI_ROW: readonly TauntItem[] = [
  { id: "emoji_thumb", kind: "emoji", label: "Nice", text: "\u{1F44D}" },
  { id: "emoji_laugh", kind: "emoji", label: "Laugh", text: "\u{1F602}" },
  { id: "emoji_wow", kind: "emoji", label: "Wow", text: "\u{1F62E}" },
  { id: "emoji_angry", kind: "emoji", label: "Grr", text: "\u{1F621}" },
  { id: "emoji_hat", kind: "emoji", label: "Howdy", text: "\u{1F920}" },
  { id: "emoji_skull", kind: "emoji", label: "Busted", text: "\u{1F480}" },
  { id: "emoji_fire", kind: "emoji", label: "Heater", text: "\u{1F525}" },
  { id: "emoji_joker", kind: "emoji", label: "Card", text: "\u{1F0CF}" },
];

/**
 * The three `taunt` cosmetics `0007_cosmetics.sql` seeds, with the line each shows
 * until its sticker exists.
 *
 * The ids are the migration's, not new ones: this phase gives the `taunt` KIND a
 * renderer, and building a second catalogue beside the one in the database is how the
 * two come to disagree. Nothing here grants, sells or equips anything — that is the
 * purchase path, and it is not this phase's.
 */
export const SEEDED_TAUNTS: readonly TauntItem[] = [
  { id: "taunt_tip_hat", kind: "line", label: "Tip of the Hat", text: "Tip of the hat.", sticker: "sticker-01-tiphat" },
  { id: "taunt_slow_clap", kind: "line", label: "Slow Clap", text: "Slow… clap.", sticker: "sticker-02-slowclap" },
  { id: "taunt_spit", kind: "line", label: "Spit and Sigh", text: "Hmph.", sticker: "sticker-03-spittoon" },
];

/** Every taunt the hero's tray can offer, before the sticker set is folded in. */
export const HERO_TAUNTS: readonly TauntItem[] = [...SEEDED_TAUNTS, ...EMOJI_ROW];

const BY_ID = new Map<string, TauntItem>(HERO_TAUNTS.map((t) => [t.id, t]));

/** A bot's line, as a taunt item. The id carries the archetype so a replay matches. */
export function lineTaunt(archetype: ArchetypeId, index: number): TauntItem {
  const set = ARCHETYPE_LINES[archetype];
  const line = set[index % set.length]!;
  return { id: `line_${archetype}_${index % set.length}`, kind: "line", label: line.text, text: line.text };
}

/* --------------------------------------------- Phase 7 box 0(6): the `up` pool --- */

/**
 * The `up` lines, SHARED: any character may say any of them.
 *
 * ======================= WHY THE WINNING LINES ARE POOLED =======================
 * Phase 6's open question 1: a player who learned the fifty-four lines could read an
 * archetype off a bubble, and the archetype is what the product hides until the
 * Ledger. The planning review's answer was to keep the per-archetype FREQUENCIES —
 * a Rock's silence on a fold-win is a tell, and reading tells is the skill — and to
 * pool the WORDS for the moment that is most frequent and says the most: winning.
 * So the words now say who is talking (a voice, a face) and nothing about how they
 * play. Busts and the timeout nudge stay archetype-specific: they are rare, they are
 * the lines with the most character in them, and a bust is the one moment the table
 * is already about to learn something about that seat anyway.
 *
 * The pool is built FROM `ARCHETYPE_LINES` rather than typed out a second time, so
 * the copy still lives in one place and still passes the four rules above; each
 * archetype's three `up` lines are its contribution to the pool, not its own.
 * The pool's order is the archetypes' declaration order, and it is fixed: a line's
 * id is its index here (`line_up_<k>`), and a replay has to find the same words.
 * ================================================================================
 */
export const ARCHETYPE_IDS: readonly ArchetypeId[] = Object.keys(ARCHETYPE_LINES) as ArchetypeId[];

export const UP_POOL: readonly string[] = ARCHETYPE_IDS.flatMap(
  (a) => ARCHETYPE_LINES[a].filter((l) => l.mood === "up").map((l) => l.text));

/** One line of the shared pool, as a taunt item. */
export function upTaunt(index: number): TauntItem {
  const k = ((index % UP_POOL.length) + UP_POOL.length) % UP_POOL.length;
  const text = UP_POOL[k]!;
  return { id: `line_up_${k}`, kind: "line", label: text, text };
}

/**
 * What a seat of this archetype may say in this mood, as taunt items.
 *
 * `up` is the shared pool for every archetype; `down` and `nudge` are the
 * archetype's own. This, not `linesFor`, is what the voice draws from. `linesFor`
 * still answers "which lines did this archetype contribute", which is what the copy
 * tests read.
 */
export function choicesFor(archetype: ArchetypeId, mood: Mood): TauntItem[] {
  if (mood === "up") return UP_POOL.map((_, k) => upTaunt(k));
  return linesFor(archetype, mood).map((x) => lineTaunt(archetype, x.index));
}

/** Look one up by id. Unknown ids draw nothing rather than an empty bubble. */
export function tauntById(id: string): TauntItem | null {
  const known = BY_ID.get(id);
  if (known) return known;
  const up = /^line_up_(\d+)$/.exec(id);
  if (up) return Number(up[1]) < UP_POOL.length ? upTaunt(Number(up[1])) : null;
  const m = /^line_([a-z]+)_(\d+)$/.exec(id);
  if (m && m[1]! in ARCHETYPE_LINES) return lineTaunt(m[1] as ArchetypeId, Number(m[2]));
  return null;
}

/** The lines of one mood, for one archetype. Never empty — the test holds that. */
export function linesFor(archetype: ArchetypeId, mood: Mood): { line: Line; index: number }[] {
  const set = ARCHETYPE_LINES[archetype];
  return set.map((line, index) => ({ line, index })).filter((x) => x.line.mood === mood);
}
